import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, SectionHeading } from "./Reveal";
import { CalendarCheck, ExternalLink, Send, Phone, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CALENDLY = [
  { label: "Diagnostic offert — 1er rendez-vous", url: "https://calendly.com/contact-frederiquevasseur/diagnostic-offert", testId: "calendly-diagnostic-button" },
  { label: "Séance individuelle — 1h", url: "https://calendly.com/contact-frederiquevasseur/seance-individuelle", testId: "calendly-individuel-button" },
  { label: "Séance en entreprise — 1h30", url: "https://calendly.com/contact-frederiquevasseur/seance-entreprise", testId: "calendly-entreprise-button" },
];

const inputCls =
  "w-full rounded-md border border-[#D4AF37]/20 bg-[#0A1120]/80 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-[#D4AF37]/60 transition-colors duration-300";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-2 block font-mono-accent text-[10px] uppercase tracking-[0.25em] text-slate-400">
      {label}
    </span>
    {children}
  </label>
);

export default function ContactBooking() {
  const [booking, setBooking] = useState({
    name: "", email: "", phone: "", accompaniment_type: "pme", preferred_slot: "", message: "",
  });
  const [contact, setContact] = useState({ name: "", email: "", subject: "", message: "" });
  const [sendingBooking, setSendingBooking] = useState(false);
  const [sendingContact, setSendingContact] = useState(false);

  const submitBooking = async (e) => {
    e.preventDefault();
    setSendingBooking(true);
    try {
      await axios.post(`${API}/appointments`, {
        ...booking,
        preferred_slot: booking.preferred_slot || null,
        message: booking.message || null,
      });
      toast.success("Demande de rendez-vous envoyée", {
        description: "Frédérique vous recontacte personnellement pour confirmer le créneau.",
      });
      setBooking({ name: "", email: "", phone: "", accompaniment_type: "pme", preferred_slot: "", message: "" });
    } catch {
      toast.error("Une erreur est survenue", { description: "Merci de réessayer dans un instant." });
    } finally {
      setSendingBooking(false);
    }
  };

  const submitContact = async (e) => {
    e.preventDefault();
    setSendingContact(true);
    try {
      await axios.post(`${API}/contact`, { ...contact, subject: contact.subject || null });
      toast.success("Message envoyé", { description: "Merci pour votre message, réponse sous 48h." });
      setContact({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Une erreur est survenue", { description: "Merci de réessayer dans un instant." });
    } finally {
      setSendingContact(false);
    }
  };

  return (
    <section id="contact" data-testid="booking-contact-section" className="relative py-28 lg:py-36 bg-[#0C1526] border-t border-[#D4AF37]/10">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHeading
          overtitle="Passons à l'action"
          title={<>Prendre rendez-vous, <span className="italic text-gold-gradient">ou m'écrire.</span></>}
          align="center"
        />

        <Reveal delay={0.2}>
          <div data-testid="contact-direct-info" className="mt-8 flex flex-wrap justify-center items-center gap-x-10 gap-y-4">
            <a data-testid="contact-phone-link" href="tel:+33744774445"
              className="flex items-center gap-3 text-slate-200 hover:text-[#F3E5AB] transition-colors">
              <Phone size={18} className="text-[#D4AF37]" />
              <span className="text-lg font-medium tracking-wide">07 44 77 44 45</span>
            </a>
            <span className="hidden sm:block h-5 w-px bg-[#D4AF37]/30" />
            <a data-testid="contact-email-link" href="mailto:contact@frederiquevasseur.com"
              className="flex items-center gap-3 text-slate-200 hover:text-[#F3E5AB] transition-colors">
              <Mail size={18} className="text-[#D4AF37]" />
              <span className="text-lg font-medium tracking-wide">contact@frederiquevasseur.com</span>
            </a>
          </div>
        </Reveal>

        <div className="mt-16 grid lg:grid-cols-2 gap-6">
          <Reveal>
            <div className="h-full rounded-2xl border border-[#D4AF37]/30 bg-[#111C33] p-8 lg:p-10">
              <div className="flex items-center gap-3">
                <CalendarCheck size={24} className="text-[#D4AF37]" strokeWidth={1.5} />
                <h3 className="font-serif-display text-2xl text-white">Prendre rendez-vous en ligne</h3>
              </div>
              <p className="mt-3 text-sm text-slate-400 leading-relaxed">
                Premier rendez-vous diagnostic offert : nous établissons ensemble votre besoin et son
                urgence. Indiquez vos disponibilités, je vous confirme le créneau personnellement.
              </p>

              <form onSubmit={submitBooking} className="mt-8 space-y-5" data-testid="booking-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Nom complet">
                    <input data-testid="booking-name-input" required className={inputCls} value={booking.name}
                      onChange={(e) => setBooking({ ...booking, name: e.target.value })} placeholder="Votre nom" />
                  </Field>
                  <Field label="Téléphone">
                    <input data-testid="booking-phone-input" required type="tel" className={inputCls} value={booking.phone}
                      onChange={(e) => setBooking({ ...booking, phone: e.target.value })} placeholder="06 00 00 00 00" />
                  </Field>
                </div>
                <Field label="Email">
                  <input data-testid="booking-email-input" required type="email" className={inputCls} value={booking.email}
                    onChange={(e) => setBooking({ ...booking, email: e.target.value })} placeholder="vous@exemple.fr" />
                </Field>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Type d'accompagnement">
                    <select data-testid="booking-type-select" className={inputCls} value={booking.accompaniment_type}
                      onChange={(e) => setBooking({ ...booking, accompaniment_type: e.target.value })}>
                      <option value="pme">Dirigeant / Entreprise (QVT)</option>
                      <option value="individuel">Accompagnement individuel</option>
                      <option value="autre">Autre / Je ne sais pas encore</option>
                    </select>
                  </Field>
                  <Field label="Créneau souhaité">
                    <input data-testid="booking-slot-input" className={inputCls} value={booking.preferred_slot}
                      onChange={(e) => setBooking({ ...booking, preferred_slot: e.target.value })} placeholder="Ex : mardi matin" />
                  </Field>
                </div>
                <Field label="Votre besoin en quelques mots (optionnel)">
                  <textarea data-testid="booking-message-textarea" rows={3} className={inputCls} value={booking.message}
                    onChange={(e) => setBooking({ ...booking, message: e.target.value })} placeholder="Dites-m'en un peu plus…" />
                </Field>
                <button
                  data-testid="booking-form-submit-button"
                  type="submit"
                  disabled={sendingBooking}
                  className="w-full rounded-full bg-[#D4AF37] px-8 py-4 text-sm font-semibold text-[#0A1120] transition-all duration-300 hover:bg-[#F3E5AB] disabled:opacity-50"
                >
                  {sendingBooking ? "Envoi en cours…" : "Envoyer ma demande de rendez-vous"}
                </button>
                <div className="pt-2">
                  <p className="text-center font-mono-accent text-[10px] uppercase tracking-[0.25em] text-slate-500">
                    Ou choisissez directement votre créneau
                  </p>
                  <div className="mt-4 space-y-3">
                    {CALENDLY.map((c) => (
                      <a
                        key={c.url}
                        data-testid={c.testId}
                        href={c.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex items-center justify-between gap-3 rounded-full border border-[#D4AF37]/35 px-6 py-3.5 text-sm text-[#F3E5AB] transition-all duration-300 hover:bg-[#D4AF37] hover:text-[#0A1120] hover:border-[#D4AF37]"
                      >
                        <span className="font-medium">{c.label}</span>
                        <ExternalLink size={15} className="shrink-0 transition-transform duration-300 group-hover:translate-x-0.5" />
                      </a>
                    ))}
                  </div>
                </div>
              </form>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="h-full rounded-2xl border border-[#D4AF37]/15 bg-[#0A1120]/70 p-8 lg:p-10">
              <div className="flex items-center gap-3">
                <Send size={22} className="text-[#D4AF37]" strokeWidth={1.5} />
                <h3 className="font-serif-display text-2xl text-white">M'écrire directement</h3>
              </div>
              <p className="mt-3 text-sm text-slate-400 leading-relaxed">
                Une question, un projet, une hésitation ? Ce message arrive directement dans ma boîte.
              </p>

              <form onSubmit={submitContact} className="mt-8 space-y-5" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Nom complet">
                    <input data-testid="contact-name-input" required className={inputCls} value={contact.name}
                      onChange={(e) => setContact({ ...contact, name: e.target.value })} placeholder="Votre nom" />
                  </Field>
                  <Field label="Email">
                    <input data-testid="contact-email-input" required type="email" className={inputCls} value={contact.email}
                      onChange={(e) => setContact({ ...contact, email: e.target.value })} placeholder="vous@exemple.fr" />
                  </Field>
                </div>
                <Field label="Sujet (optionnel)">
                  <input data-testid="contact-subject-input" className={inputCls} value={contact.subject}
                    onChange={(e) => setContact({ ...contact, subject: e.target.value })} placeholder="Objet de votre message" />
                </Field>
                <Field label="Votre message">
                  <textarea data-testid="contact-message-textarea" required rows={6} className={inputCls} value={contact.message}
                    onChange={(e) => setContact({ ...contact, message: e.target.value })} placeholder="Bonjour Frédérique…" />
                </Field>
                <button
                  data-testid="contact-form-submit-button"
                  type="submit"
                  disabled={sendingContact}
                  className="w-full rounded-full border border-[#D4AF37]/50 px-8 py-4 text-sm font-medium text-[#F3E5AB] transition-all duration-300 hover:bg-[#D4AF37] hover:text-[#0A1120] disabled:opacity-50"
                >
                  {sendingContact ? "Envoi en cours…" : "Envoyer mon message"}
                </button>
              </form>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
